/**
 * A tabular dataset: a 2D grid of numeric cells addressed by named rows and
 * named columns. Unlike the spatial `*TileGrid` types (which model raster
 * data over the map), a `TableDataset` is a plain matrix — e.g. a
 * cross-tabulation, a per-class lookup, or summary statistics — and is not
 * tied to a tile grid / zoom level.
 *
 * Cells are stored row-major as `data[rowIndex][columnIndex]`. Missing /
 * unset cells use `NaN`.
 */

export interface TableDatasetJSON {
  type: "TableDataset"
  /** Column header labels, left-to-right. */
  columns: string[]
  /** Row header labels, top-to-bottom. */
  rows: string[]
  /** Cell values addressed as `data[rowIndex][columnIndex]`. */
  data: number[][]
  name?: string
}

export interface TableDatasetStats {
  type: "TableDataset"
  rowCount: number
  columnCount: number
  min: number
  max: number
}

export class TableDataset {
  rows: string[]
  columns: string[]
  /** Row-major cell storage: `data[rowIndex][columnIndex]`. */
  data: number[][]
  name: string | undefined

  constructor(
    rows: string[],
    columns: string[],
    data?: number[][],
    name?: string
  ) {
    this.rows = rows
    this.columns = columns
    this.name = name

    if (data) {
      if (data.length !== rows.length) {
        throw new TypeError(
          `TableDataset: expected ${rows.length} data rows, got ${data.length}`
        )
      }
      for (const row of data) {
        if (row.length !== columns.length) {
          throw new TypeError(
            `TableDataset: expected ${columns.length} columns per row, got ${row.length}`
          )
        }
      }
      this.data = data
    } else {
      this.data = rows.map(() => new Array<number>(columns.length).fill(NaN))
    }
  }

  get rowCount(): number {
    return this.rows.length
  }

  get columnCount(): number {
    return this.columns.length
  }

  private validateIndices(rowIndex: number, columnIndex: number) {
    if (rowIndex < 0 || rowIndex >= this.rows.length) {
      throw new TypeError(`TableDataset: row index ${rowIndex} out of range`)
    }
    if (columnIndex < 0 || columnIndex >= this.columns.length) {
      throw new TypeError(`TableDataset: column index ${columnIndex} out of range`)
    }
  }

  get(rowIndex: number, columnIndex: number): number {
    this.validateIndices(rowIndex, columnIndex)
    return this.data[rowIndex][columnIndex]
  }

  set(rowIndex: number, columnIndex: number, value: number) {
    this.validateIndices(rowIndex, columnIndex)
    this.data[rowIndex][columnIndex] = value
  }

  getByName(rowName: string, columnName: string): number | undefined {
    const rowIndex = this.rows.indexOf(rowName)
    const columnIndex = this.columns.indexOf(columnName)
    if (rowIndex === -1 || columnIndex === -1) return undefined
    return this.data[rowIndex][columnIndex]
  }

  setByName(rowName: string, columnName: string, value: number): boolean {
    const rowIndex = this.rows.indexOf(rowName)
    const columnIndex = this.columns.indexOf(columnName)
    if (rowIndex === -1 || columnIndex === -1) return false
    this.data[rowIndex][columnIndex] = value
    return true
  }

  /** Returns a copy of the values in a row, left-to-right. */
  getRow(rowIndex: number): number[] {
    if (rowIndex < 0 || rowIndex >= this.rows.length) {
      throw new TypeError(`TableDataset: row index ${rowIndex} out of range`)
    }
    return [...this.data[rowIndex]]
  }

  /** Returns a copy of the values in a column, top-to-bottom. */
  getColumn(columnIndex: number): number[] {
    if (columnIndex < 0 || columnIndex >= this.columns.length) {
      throw new TypeError(`TableDataset: column index ${columnIndex} out of range`)
    }
    return this.data.map(row => row[columnIndex])
  }

  iterate(callback: (rowIndex: number, columnIndex: number, value: number) => void) {
    for (let r = 0; r < this.rows.length; r++) {
      for (let c = 0; c < this.columns.length; c++) {
        callback(r, c, this.data[r][c])
      }
    }
  }

  getStats(): TableDatasetStats {
    let min = Infinity
    let max = -Infinity
    this.iterate((_r, _c, value) => {
      if (Number.isNaN(value)) return
      if (value < min) min = value
      if (value > max) max = value
    })
    return {
      type: "TableDataset",
      rowCount: this.rowCount,
      columnCount: this.columnCount,
      min: Number.isFinite(min) ? min : NaN,
      max: Number.isFinite(max) ? max : NaN,
    }
  }

  toJSON(): TableDatasetJSON {
    return {
      type: "TableDataset",
      columns: [...this.columns],
      rows: [...this.rows],
      data: this.data.map(row => [...row]),
      name: this.name,
    }
  }

  static fromJSON(json: TableDatasetJSON): TableDataset {
    return new TableDataset(
      [...json.rows],
      [...json.columns],
      json.data.map(row => [...row]),
      json.name
    )
  }
}
